import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Cpu, 
  Zap, 
  Clock, 
  Settings2,
  GitMerge,
  MessageSquare,
  ArrowRight,
  TrendingUp,
  Activity,
  CheckCircle2,
  ShoppingBag,
  RotateCcw,
  Mail
} from 'lucide-react';
import './Automations.css';

const Automations = () => {
  const [selectedFlow, setSelectedFlow] = useState('Cart Recovery');

  const timelineCards = [
    { time: 'Day 1', label: 'Soft Nudge', subtext: 'Dynamic 10% discount offered via personal DM.', color: 'purple', metrics: { active: '842', conv: '8.1' } },
    { time: 'Day 3', label: 'Final Incentive', subtext: 'Free Express Shipping + FOMO stock alert.', color: 'emerald', metrics: { active: '215', conv: '15.6' } },
    { time: 'VIP Only', label: 'Discount offer sent in Email', subtext: 'Automated high-conversion discount code delivered via priority email sequence.', color: 'orange', metrics: { active: '42', conv: '34.0' } },
    { time: 'Post-Buy', label: 'Post Purchase Follow-up', subtext: 'Thank you note + request for review in exchange for credit.', color: 'cyan', metrics: { active: '2,840', conv: '22.5' } },
    { time: 'Urgent', label: 'Abandoned Offer Follow-up', subtext: 'Final chance DM for unused dynamic offers expiring soon.', color: 'rose', metrics: { active: '1,502', conv: '18.2' } },
  ];

  const automations = [
    { name: 'Cart Recovery (High AOV)', trigger: 'Abandoned > $150', status: 'Running', performance: '+24%', lastActive: '2m ago' },
    { name: 'VIP Welcome Sequence', trigger: 'First Purchase > $500', status: 'Running', performance: '+18%', lastActive: '12m ago' },
    { name: 'Dormant Re-engagement', trigger: 'Inactive > 30 Days', status: 'Paused', performance: '+5.2%', lastActive: '2h ago' },
    { name: 'Flash Sale Pulse', trigger: 'Manual Trigger', status: 'Draft', performance: '--', lastActive: 'Never' },
  ];

  const flowSteps = [
    { icon: Zap, label: 'Trigger Event', val: 'Cart Abandoned', color: 'blue' },
    { icon: Cpu, label: 'AI Diagnostic', val: 'Wait 1h → Stock Check', color: 'purple' },
    { icon: MessageSquare, label: 'Dynamic Action', val: 'Send "Glow" DM', color: 'cyan' },
    { icon: CheckCircle2, label: 'Conversion Goal', val: 'Purchase Value >$150', color: 'emerald' }
  ];

  const getTimelineIcon = (label) => {
    const l = label.toLowerCase();
    if (l.includes('purchase')) return ShoppingBag;
    if (l.includes('abandoned')) return RotateCcw;
    if (l.includes('email')) return Mail;
    return Clock;
  };

  return (
    <>
      <Helmet>
        <title>Automations - Revexia AI Revenue Brain</title>
        <meta name="description" content="AI-powered automation sequences and workflows" />
      </Helmet>

      <div className="automations-page-wrapper">
        <div className="container-fluid px-4">
          {/* Strategy Timelines */}
          <section className="timelines-section">
            <div className="section-header-with-icon">
              <Activity size={20} className="section-icon cyan" />
              <h3 className="section-main-title">Strategy Timelines</h3>
            </div>
            <div className="row g-4">
              {timelineCards.map((card, index) => {
                const Icon = getTimelineIcon(card.label);
                return (
                  <div key={index} className="col-12 col-md-6 col-xl-4">
                    <div className={`timeline-card timeline-${card.color}`}>
                      <div className="timeline-card-header">
                        <div className={`timeline-card-icon ${card.color}`}>
                          <Icon size={18} />
                        </div>
                        <span className="timeline-card-time">{card.time}</span>
                      </div>
                      <div className="timeline-card-body">
                        <h4 className="timeline-card-title">{card.label}</h4>
                        <p className="timeline-card-subtext">{card.subtext}</p>
                      </div>
                      <div className="timeline-card-footer">
                        <div className="timeline-metric">
                          <p className="timeline-metric-label">Active Users</p>
                          <p className="timeline-metric-value">{card.metrics.active}</p>
                        </div>
                        <div className="timeline-divider"></div>
                        <div className="timeline-metric">
                          <p className="timeline-metric-label">Conv. Rate</p>
                          <p className={`timeline-metric-value timeline-${card.color}`}>{card.metrics.conv}%</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Main Control Grid */}
          <div className="row g-4 mb-5">
            {/* Automation List */}
            <div className="col-12 col-lg-8">
              <div className="automation-management-card">
                <div className="automation-management-header">
                  <div>
                    <h3 className="automation-management-title">Management Engine</h3>
                    <p className="automation-management-subtitle">Configure and monitor active AI sequences</p>
                  </div>
                  <button className="automation-settings-btn">
                    <Settings2 size={18} />
                  </button>
                </div>
                <div className="automation-table-wrapper">
                  <table className="automation-table">
                    <thead>
                      <tr>
                        <th>Automation / History</th>
                        <th>Trigger Type</th>
                        <th>Status</th>
                        <th className="text-end">ROI Uplift</th>
                      </tr>
                    </thead>
                    <tbody>
                      {automations.map((auto) => (
                        <tr 
                          key={auto.name} 
                          onClick={() => setSelectedFlow(auto.name)}
                          className="automation-table-row"
                        >
                          <td>
                            <p className="automation-name">{auto.name}</p>
                            <p className="automation-last-active">Active: {auto.lastActive}</p>
                          </td>
                          <td>
                            <div className="automation-trigger">
                              <Zap size={14} className="trigger-icon" /> {auto.trigger}
                            </div>
                          </td>
                          <td>
                            <span className={`automation-status-badge status-${auto.status.toLowerCase()}`}>
                              {auto.status}
                            </span>
                          </td>
                          <td className="text-end">
                            <p className={`automation-performance ${auto.performance.startsWith('+') ? 'positive' : 'neutral'}`}>
                              {auto.performance}
                            </p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Visual Flow Preview */}
            <div className="col-12 col-lg-4">
              <div className="logic-canvas-card">
                <div className="logic-canvas-header">
                  <div className="logic-canvas-icon">
                    <GitMerge size={20} />
                  </div>
                  <h3 className="logic-canvas-title">Logic Canvas</h3>
                </div>

                <div className="flow-steps-container">
                  <div className="flow-steps-line"></div>
                  {flowSteps.map((step, i) => (
                    <div key={i} className="flow-step-item">
                      <div className={`flow-step-icon ${step.color}`}>
                        <step.icon size={16} />
                      </div>
                      <div className="flow-step-content">
                        <p className="flow-step-label">{step.label}</p>
                        <p className="flow-step-value">{step.val}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <button className="simulation-mode-btn">
                  <span>Simulation Mode</span>
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>

          {/* Recommendations Footer */}
          <div className="row g-4">
            <div className="col-12 col-lg-8">
              <div className="ai-recommendation-card">
                <div className="ai-recommendation-icon">
                  <TrendingUp size={20} />
                </div>
                <div className="ai-recommendation-content">
                  <p className="ai-recommendation-label">AI Recommendation Engine</p>
                  <p className="ai-recommendation-text">
                    Add an <span className="highlight-text">"Abandoned Checkout"</span> secondary trigger to the Cart Recovery flow. Revexia predicts a cumulative ROI uplift of <span className="highlight-emerald">+8.2%</span> over the next billing cycle.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Automations;
